import clsx from 'clsx';
import React, { MouseEventHandler, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HelpOutlinedIcon } from 'icons';
import { IconButton, Menu, styled } from 'netline-ui';
import services from 'services';
import { HelpTopicWithKey } from 'types';
import Divider from 'view/components/Divider/Divider';

import styles, {
  helpMenuClasses,
  helpMenuIconClasses,
  helpMenuIconRootClasses,
  helpMenuIconStyles,
} from './HelpMenu.styles';
import HelpMenuItem, { HelpIconType } from './HelpMenuItem';
import useHelpTopics from './useHelpTopics';

const HelpMenuIconRoot = styled(IconButton, { name: helpMenuIconClasses.$name })(helpMenuIconStyles);
const HelpMenuRoot = styled(Menu, { name: helpMenuClasses.$name })(styles);

const HelpMenu = () => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleToggleMenuClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => setAnchorEl(event.currentTarget),
    [],
  );
  const onMenuClose = useCallback(() => setAnchorEl(null), []);

  const onMenuItemClick = useCallback((topic: HelpTopicWithKey) => {
    services.appFrame.help.openTopic(topic.key);
    setAnchorEl(null);
  }, []);

  const helpTopics = useHelpTopics();
  const { defaultTopics, contextTopics } = helpTopics;
  const defaultTopicsCount = defaultTopics.length;
  const contextTopicsCount = contextTopics.length;
  const count = defaultTopicsCount + contextTopicsCount;

  return (
    <>
      <HelpMenuIconRoot
        className={clsx(helpMenuIconClasses.root, { [helpMenuIconRootClasses.hideIcon]: !count })}
        onClick={handleToggleMenuClick}
        data-testid="HelpMenu-toggleButton"
      >
        <HelpOutlinedIcon />
      </HelpMenuIconRoot>
      <HelpMenuRoot
        className={helpMenuClasses.root}
        keepMounted
        classes={{ paper: helpMenuClasses.paper }}
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {!!contextTopicsCount && <div className={helpMenuClasses.contextTitle}>{t('help.related_articles')}</div>}
        {contextTopics.map((topic) => (
          <HelpMenuItem key={topic.key} topic={topic} iconType={HelpIconType.Subject} onClick={onMenuItemClick} />
        ))}
        {defaultTopicsCount > 0 && contextTopicsCount > 0 && <Divider />}
        {defaultTopics.map((topic) => (
          <HelpMenuItem key={topic.key} topic={topic} iconType={HelpIconType.Popup} onClick={onMenuItemClick} />
        ))}
      </HelpMenuRoot>
    </>
  );
};
export default HelpMenu;
